/* ==========================================================================
   Getting in

   A phone number, and the code sent to it. That is the whole of it, for
   signing up and for coming back: the number is the account. No password to
   forget, no invite to hand out — and, as before, nothing immigration-
   adjacent. Decision 6 still holds: no document, ever.

   One number is one side. A phone with a home account cannot start a cleaner
   account, or the other way round; to switch, delete and start again. The
   server enforces it — this screen only explains it.

   The one exception is an admin's number, which may hold an account on each
   side. Then there is nothing to infer from the number alone, and this screen
   asks which one to open rather than letting the server pick.
   ========================================================================== */

import { useState } from 'react';
import { Icon } from '../components/Icon';
import { TownPicks } from '../components/TownPicks';
import { Wordmark } from '../components/ui';
import { t } from '../lib/i18n';
import {
  checkPhone, setWantsSubscribe, signInDemo, signInExisting, startPhone,
  startSubscription, state,
} from '../store';

type Step = 'phone' | 'code' | 'signincode';
type Side = 'house' | 'cleaner';

export function SignIn() {
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  /* Texts about their own jobs. Starts unticked, always: a box ticked for
     somebody is not their agreement, and the carriers refuse it. */
  const [textsOk, setTextsOk] = useState(false);
  const [code, setCode] = useState('');
  const [side, setSide] = useState<Side | null>(null);
  const [name, setName] = useState('');
  /* What the server said when it sent the code: whether this number is
     already known, and whether the code went by text or has to be fetched
     from an admin. */
  const [known, setKnown] = useState<{ role: Side | null; delivery: 'sms' | 'admin' } | null>(null);
  const [needDetails, setNeedDetails] = useState(false);
  /* Where they are. Asked here rather than on the profile afterwards, because
     afterwards turned out to mean never: both real cleaners in production
     reached the directory with no postal code and no areas, which makes a
     listing nobody can judge or find. */
  const [zip, setZip] = useState('');
  const [address, setAddress] = useState('');
  const [areas, setAreas] = useState<string[]>([]);
  /* Free month or pay today. Offered at sign-up rather than sprung on
     somebody in week four -- being told about a charge the first time it
     stops you is how an app earns a bad review it did not need. */
  const [plan, setPlan] = useState<'trial' | 'subscribe'>('trial');
  /* The geocoder did not recognise the address. They get to insist. */
  const [addressDoubt, setAddressDoubt] = useState(false);
  /* A promo from Oster, or a friend's share code. One box: whoever is holding
     one does not know which kind it is, and asking them to pick the right
     field is asking them to get it wrong. */
  const [promo, setPromo] = useState(() => {
    /* A shared link carries the code: ozeret.app/?code=K4P7QX. Filling the
       box from it is the difference between "tap the link, tap sign up" and
       "tap the link, then go back to the message to copy the code out of it",
       which is where most of these are lost. */
    try {
      return (new URLSearchParams(window.location.search).get('code') ?? '')
        .toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 24);
    } catch { return ''; }
  });
  /* This number has an account on each side, so the code alone does not say
     who is signing in. Ask, and send the answer with the code. */
  const [askSide, setAskSide] = useState(false);
  /* Either way the side is wanted; only a new number also needs a name. */
  const needSide = needDetails || askSide;

  /* The bootstrap carries billing with nobody signed in, so these are the
     real figures rather than a copy that can drift from the server. */
  const price = state.billing?.price ?? '$2.99';
  const trialDays = state.billing?.trialDays ?? 30;

  const digits = phone.replace(/\D/g, '');
  const phoneOk = digits.length === 10 || (digits.length === 11 && digits.startsWith('1'))
    || (phone.trim().startsWith('+') && digits.length >= 8);

  const send = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!phoneOk || state.busy) return;
    const r = await startPhone(phone);
    if (r) {
      setKnown({ role: (r.role as Side | null) ?? null, delivery: r.delivery });
      setNeedDetails(!r.known);
      setAskSide(r.roles.length > 1);
      setStep('code');
    }
  };

  const verify = async (e?: React.FormEvent, insist = false) => {
    e?.preventDefault();
    if (state.busy) return;
    const r = await checkPhone(
      phone, code, needSide ? side : null, needDetails ? name : '',
      {
        ...(needDetails
          ? {
            zip,
            address: side === 'house' ? address : '',
            areas: side === 'cleaner' ? areas : [],
            addressConfirmed: insist,
            promo: promo.trim(),
          }
          : {}),
        smsOk: textsOk,
        lang: state.lang,
      },
    );
    /* The server may say it is missing something: a side and a name because
       this number is new, or just a side because the number holds an account
       on each. Show those fields rather than sending it round again -- the
       code the user typed is still good, so the next press signs them in. */
    if (r === 'need-details') setNeedDetails(true);
    if (r === 'need-side') setAskSide(true);
    setAddressDoubt(r === 'address-doubt');

    /* Signed up and asked to pay today: straight to Stripe. If that fails
       they are still signed in on the free month and the toast says why --
       the wrong way to fail here would be to leave them with no account.

       setWantsSubscribe first, and deliberately not cleared here: if the
       redirect never lands the app shows them a Subscribe bar the moment
       they arrive, instead of leaving them on a free month they did not
       choose with no sign anything was meant to happen. */
    if (r === true && needDetails && plan === 'subscribe') {
      setWantsSubscribe(true);
      void startSubscription();
    }
  };

  /* The same rules the server keeps, so the button is honest about what it
     will do. The server checks them again regardless: this endpoint is
     reachable without the app, and a disabled button is not a rule. */
  const zipOk = zip.replace(/\D/g, '').length === 5;
  const placeOk = !needDetails || side === null
    || (zipOk && (side === 'house' ? address.trim().length >= 4 : areas.length > 0));

  const codeReady = code.replace(/\D/g, '').length >= 4
    && (!needSide || side !== null)
    && (!needDetails || name.trim().length >= 2)
    && placeOk;

  /* What is still missing, in words, under a button that will not press.

     Oster typed "Ppp" as a street address and reported that there was no
     address validation. There is -- but four characters is the minimum, so
     the button simply went grey and said nothing, and he never got as far as
     the server check. A disabled control that will not explain itself reads
     as a broken app, and the person is left guessing which of six fields it
     is unhappy about. */
  const missing: string[] = [];
  if (code.replace(/\D/g, '').length < 4) missing.push(t('s_need_code'));
  if (needSide && side === null) missing.push(t('s_need_side'));
  if (needDetails && name.trim().length < 2) missing.push(t('s_need_name'));
  if (needDetails && side !== null) {
    if (!zipOk) missing.push(t('s_need_zip'));
    if (side === 'house' && address.trim().length < 4) missing.push(t('s_need_address'));
    if (side === 'cleaner' && !areas.length) missing.push(t('s_need_areas'));
  }

  /* The permanent code, for a second device -- and, while texts are not
     switched on, the way back in that does not depend on anybody reading a
     number out of a database. Removing this door was how Oster locked
     himself out of his own app an hour after signing up. */
  const [signinCode, setSigninCode] = useState('');
  const submitSignin = (e: React.FormEvent) => {
    e.preventDefault();
    if (signinCode.trim().length >= 6 && !state.busy) void signInExisting(signinCode);
  };

  return (
    <div className="screen-pad signin" style={{ paddingTop: 28 }}>
      <div className="col" style={{ gap: 6, alignItems: 'center', textAlign: 'center' }}>
        <Wordmark size={30} />
        <span className="sub">{t('s_tagline')}</span>
      </div>

      {step === 'phone' && (
        <div className="card" style={{ marginTop: 18 }}>
          <form onSubmit={send} style={{ display: 'contents' }}>
            <div className="field">
              <label>{t('s_yournumber')}</label>
              <input
                className="input"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="(631) 555-0123"
                value={phone}
                autoFocus
                onChange={(e) => setPhone(e.target.value)}
              />
              <span className="hint">{t('s_numberhint')}</span>
            </div>

            {/* Optional, unticked, and separate from signing up: the only
                kind of agreement to texts the carriers accept. */}
            <label className="textsok" htmlFor="texts-ok">
              <input
                id="texts-ok"
                type="checkbox"
                checked={textsOk}
                onChange={(e) => setTextsOk(e.target.checked)}
              />
              <span>
                <b>{t('s_textsok')}</b>{' '}
                <span className="hint">
                  {t('s_textsfine')}{' '}
                  <a href="/terms" target="_blank" rel="noreferrer">{t('s_terms')}</a>
                  {' · '}
                  <a href="/privacy" target="_blank" rel="noreferrer">{t('s_privacy')}</a>
                </span>
              </span>
            </label>

            {state.signInError && (
              <div className="note warm"><b>{state.signInError}</b></div>
            )}

            <button className="btn lg block" disabled={!phoneOk || state.busy}>
              {state.busy && !state.busyWith?.startsWith('demo') ? t('s_onemoment') : t('s_sendcode')}
            </button>
          </form>
        </div>
      )}

      {step === 'code' && (
        <div className="card" style={{ marginTop: 18 }}>
          <form onSubmit={verify} style={{ display: 'contents' }}>
            <div className="field">
              <label>{t('s_thecode')}</label>
              <input
                className="input mono"
                style={{ letterSpacing: '.22em', fontSize: 20 }}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="••••••"
                value={code}
                autoFocus
                maxLength={8}
                onChange={(e) => setCode(e.target.value)}
              />
              <span className="hint">
                {known?.delivery === 'sms'
                  ? t('s_codesent')
                  : t('s_codeadmin')}
              </span>
            </div>

            {needSide && (
              <div className="field">
                <label>{askSide ? t('s_whichaccount') : t('s_whichside')}</label>
                <div className="row" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className={'btn grow ' + (side === 'house' ? '' : 'ghost')}
                    onClick={() => setSide('house')}
                  >
                    {askSide ? t('s_myhome') : t('s_ineedcleaner')}
                  </button>
                  <button
                    type="button"
                    className={'btn grow ' + (side === 'cleaner' ? '' : 'ghost')}
                    onClick={() => setSide('cleaner')}
                  >
                    {askSide ? t('s_mycleaning') : t('s_icleanhomes')}
                  </button>
                </div>
                <span className="hint">{askSide ? t('s_twoaccounts') : t('s_oneside')}</span>
              </div>
            )}

            {needDetails && (
              <div className="field">
                <label>{t('s_callyou')}</label>
                <input
                  className="input"
                  placeholder={t('s_nameplaceholder')}
                  value={name}
                  maxLength={60}
                  onChange={(e) => setName(e.target.value)}
                />
                <span className="hint">{t('s_namehint')}</span>
              </div>
            )}

            {/* Where they are.

                A home gives a postal code and a street address; a cleaner
                gives the postal code they set out from and the towns they
                will travel to. Both are required, and both are required on
                the server too -- see /api/auth/phone/check.

                The address is never shown to anybody until that home books
                somebody, which is worth saying on the form: it is the field
                people hesitate over, and the hesitation is reasonable. */}
            {needDetails && side !== null && (
              <div className="field">
                <label>{side === 'house' ? t('s_wherehome') : t('s_wherework')}</label>
                <input
                  className="input"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  placeholder={t('s_zipplaceholder')}
                  value={zip}
                  maxLength={10}
                  onChange={(e) => setZip(e.target.value)}
                />
                <span className="hint">
                  {side === 'house' ? t('s_ziphint_h') : t('s_ziphint_c')}
                </span>
              </div>
            )}

            {needDetails && side === 'house' && (
              <div className="field">
                <label>{t('s_address')}</label>
                <input
                  className="input"
                  autoComplete="street-address"
                  placeholder={t('s_addressplaceholder')}
                  value={address}
                  maxLength={120}
                  onChange={(e) => setAddress(e.target.value)}
                />
                <span className="hint">{t('s_addresshint')}</span>
                {/* Nothing could find it. Almost always a typo, occasionally a
                    real lane nobody has mapped -- so they get the last word. */}
                {addressDoubt && (
                  <button
                    type="button"
                    className="btn quiet sm"
                    style={{ marginTop: 8, alignSelf: 'flex-start' }}
                    disabled={state.busy}
                    onClick={() => void verify(undefined, true)}
                  >
                    {t('s_useanyway')}
                  </button>
                )}
              </div>
            )}

            {needDetails && side === 'cleaner' && (
              <div className="field">
                <label>{t('s_areas')}</label>
                {zipOk ? (
                  <TownPicks
                    zip={zip.replace(/\D/g, '')}
                    chosen={areas}
                    onFlip={(id) => setAreas((was) => (
                      was.includes(id) ? was.filter((a) => a !== id) : [...was, id]
                    ))}
                  />
                ) : (
                  <span className="hint">{t('s_areasneedzip')}</span>
                )}
                <span className="hint">{t('s_areashint')}</span>
              </div>
            )}

            {/* A code, if somebody gave them one.

                Last, and optional, and quiet. Most people will not have one
                and should not be made to wonder whether they are missing
                out -- but somebody Oster has handed a launch code to is
                looking for exactly this box, and it has to be findable
                without an explanation. */}
            {needDetails && (
              <div className="field">
                <label>{t('s_havepromo')}</label>
                <input
                  className="input"
                  placeholder={t('s_promoplaceholder')}
                  value={promo}
                  maxLength={24}
                  autoCapitalize="characters"
                  spellCheck={false}
                  onChange={(e) => setPromo(e.target.value.toUpperCase())}
                />
                <span className="hint">{t('s_promohint')}</span>
              </div>
            )}

            {/* What happens about money, said once, here.

                Oster: "I don't see anywhere upon signing up that this will
                only be a 30 day trial... I will be surprised the first time I
                am warned about it, the user needs to know right away when they
                sign up."

                He is right, and the surprise is the whole problem: the app
                used to say nothing until day twenty-three, when a bar appeared
                counting down to something nobody had agreed to. */}
            {needDetails && (
              <div className="field">
                <label>{t('s_howstart')}</label>
                {/* Two lines each, and the row stretches, so the two read as
                    one choice rather than a big button beside a small one:
                    "Subscribe now $2.99" wrapped and came out half again as
                    tall as its neighbour. Both say per month, because a price
                    without a period is not a price. */}
                <div className="row planrow" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className={'btn plan grow ' + (plan === 'trial' ? '' : 'ghost')}
                    onClick={() => setPlan('trial')}
                  >
                    <b>{trialDays} {t('s_daysfree')}</b>
                    <small>{t('s_thenprice').replace('{price}', price)}</small>
                  </button>
                  <button
                    type="button"
                    className={'btn plan grow ' + (plan === 'subscribe' ? '' : 'ghost')}
                    onClick={() => setPlan('subscribe')}
                  >
                    <b>{t('s_subnow')}</b>
                    <small>{t('s_permonth').replace('{price}', price)}</small>
                  </button>
                </div>
                <span className="hint">
                  {plan === 'trial'
                    ? t('s_trialhint').replace('{days}', String(trialDays)).replace('{price}', price)
                    : t('s_subhint').replace('{price}', price)}
                </span>
              </div>
            )}

            {state.signInError && (
              <div className="note warm"><b>{state.signInError}</b></div>
            )}

            <button className="btn lg block" disabled={!codeReady || state.busy}>
              {state.busy
                ? t('s_onemoment')
                : needDetails ? t('s_startusing') : t('s_signin')}
            </button>
            {!codeReady && missing.length > 0 && (
              <span className="hint" style={{ textAlign: 'center' }}>
                {t('s_stillneed')}{missing.join(', ')}.
              </span>
            )}
          </form>

          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn quiet sm grow"
              disabled={state.busy}
              onClick={() => { setCode(''); setStep('phone'); }}
            >
              {t('s_wrongnumber')}
            </button>
            <button
              type="button"
              className="btn quiet sm grow"
              disabled={state.busy}
              onClick={() => void send()}
            >
              {t('s_sendagain')}
            </button>
          </div>
        </div>
      )}

      {step === 'signincode' && (
        <div className="card" style={{ marginTop: 18 }}>
          <form onSubmit={submitSignin} style={{ display: 'contents' }}>
            <div className="field">
              <label>{t('s_yoursignincode')}</label>
              <input
                className="input mono"
                style={{ letterSpacing: '.18em', textTransform: 'uppercase', fontSize: 18 }}
                placeholder="XXXX-XXXX"
                value={signinCode}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                maxLength={12}
                autoFocus
                onChange={(e) => setSigninCode(e.target.value.toUpperCase())}
              />
              <span className="hint">{t('s_signincodehint')}</span>
            </div>

            {state.signInError && (
              <div className="note warm"><b>{state.signInError}</b></div>
            )}

            <button
              className="btn lg block"
              disabled={signinCode.trim().length < 6 || state.busy}
            >
              {state.busy ? t('s_onemoment') : t('s_signin')}
            </button>
          </form>
        </div>
      )}

      {step !== 'signincode' ? (
        <button
          type="button"
          className="btn quiet block"
          onClick={() => { state.signInError = null; setStep('signincode'); }}
        >
          {t('s_havecode')}
        </button>
      ) : (
        <button
          type="button"
          className="btn quiet block"
          onClick={() => { state.signInError = null; setStep('phone'); }}
        >
          {t('s_usenumber')}
        </button>
      )}

      <div className="note">
        <Icon name="shield" /> {t('s_nodocs')}
      </div>

      <div className="card flat">
        <span className="sub tight" style={{ fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase' }}>
          {t('s_justlooking')}
        </span>
        <span className="sub">{t('s_lookaroundhint')}</span>
        <div className="row" style={{ gap: 8 }}>
          {/* The label changes on THIS button, because this is the one that
              was pressed. It used to appear on "Send me a code" instead. */}
          <button
            type="button"
            className="btn ghost sm grow"
            disabled={state.busy}
            onClick={() => void signInDemo('house')}
          >
            {state.busyWith === 'demo-house' ? t('s_onemoment') : t('s_lookhome')}
          </button>
          <button
            type="button"
            className="btn ghost sm grow"
            disabled={state.busy}
            onClick={() => void signInDemo('cleaner')}
          >
            {state.busyWith === 'demo-cleaner' ? t('s_onemoment') : t('s_lookcleaner')}
          </button>
        </div>
      </div>

      <p className="hint" style={{ textAlign: 'center' }}>{t('s_disclaimer')}</p>
      {/* The public pages, linked from the front door so people (and search
          engines) can read what Ozeret is before signing up. */}
      <nav className="hint sitelinks" aria-label="About Ozeret">
        <a href="/how-it-works/">How it works</a>
        <a href="/cleaners/">For cleaners</a>
        <a href="/es/" lang="es">Español</a>
        <a href="/faq/">Questions</a>
      </nav>
      {/* The small print and a way to reach us, on the front door itself:
          the carriers' reviewers look here, and so do careful people. */}
      <nav className="hint sitelinks" aria-label="Terms and contact">
        <a href="/terms">Terms</a>
        <a href="/privacy">Privacy</a>
        <a href="/texts/">Text messages</a>
        <a href="mailto:hello@ozeret.app">hello@ozeret.app</a>
      </nav>
    </div>
  );
}
